import { useMemo, useState, useCallback, useEffect } from 'react'
import { FirmSectionData } from '../../../dtos/applicationData'
import { useRenewal } from '../../../context/renewal'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle,
} from '../../../components/application/defaultComponents'
import PrebuiltRenewalFooter from '../../../components/application/PrebuiltRenewalFooter'
import MainForm from 'src/components/application/mainPagesComponents/Firm/MainForm'
import { ValidationError } from 'yup'
import { FirmInfoSchema } from 'src/yup/RenewalSchema'

const Main = () => {
  const { renewalData, saveRenewalData } = useRenewal()
  const [firmData, setFirmData] = useState<FirmSectionData>(() => {
    const firmSection = renewalData?.firmSection

    if (firmSection && !firmSection.billingAddress) {
      firmSection.isBillingEqualToMail = undefined
    }
    return !!firmSection && !!firmSection?.firmName ? { ...firmSection } : {}
  })

  const [validatedFields, setValidatedFields] = useState<string[]>([])
  
  const validateSteps = useMemo(() => {
    try {
      FirmInfoSchema.validateSync(firmData, { abortEarly: false })
      return {
        step1: true,
        step2: true,
        step3: true,
        step4: true,
        step5: true,
        step6: true,
        step7: true,
        step8: true,
        step9: true,
        step10: true,
      }
    } catch (err) {
      const { errors } = err as ValidationError
      let steps: { [key: string]: boolean } = {}

      for (let stepNumber = 1; stepNumber <= 10; stepNumber++) {

        if (stepNumber === 9)
          continue;

        steps[`step${stepNumber}`] = !errors.includes(`step${stepNumber}`)
      }

      return steps
    }
  }, [firmData, FirmInfoSchema])

  const handleValidateForm = useCallback(() => {
    try {
      FirmInfoSchema.validateSync(firmData, { abortEarly: false })
      setValidatedFields([])
    } catch (err) {
      const { errors } = err as ValidationError
      const updatedErrorArray = Array.from(new Set(errors))

      if (JSON.stringify(validatedFields) !== JSON.stringify(updatedErrorArray)) {
        setValidatedFields(updatedErrorArray)
      }
    }
  }, [firmData, FirmInfoSchema, validatedFields])

  useEffect(() => {
    if (Object.keys(validatedFields).length > 0) {
      handleValidateForm()
    }
  }, [handleValidateForm, validatedFields])

  
  const updatedApplicationData = () => ({
    ...renewalData,
    currentPage: window.location.pathname,
    firmSection: { ...firmData }
  })

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent>
        <DefaultTitle>First, let’s verify some basic information about your firm.</DefaultTitle>
        <DefaultSubtitle>
          Please fill out the information below.
        </DefaultSubtitle>
        <MainForm
          firmData={firmData}
          setFirmData={setFirmData}
          validateSteps={validateSteps}
          validatedFields={validatedFields}
          renewalMode
        />
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute="/customer/renew/verify"
        isFormValid={Object.keys(validateSteps).every(key => validateSteps[key] === true)}
        nextRoute="/customer/renew/firm/practice"
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={handleValidateForm}
      />
    </ApplicationBodyContainer>
  )
}

export default Main